import { useEffect } from 'react';
import { Link } from '@tanstack/react-router';
import type { IconType } from 'react-icons';
import { FiCheckCircle } from 'react-icons/fi';
import { FaApple, FaGooglePlay } from 'react-icons/fa';
import { Footer, MarketingHeader, ProductFactBlurb, SEO } from '@/components';
import { track } from '@/lib/analytics';
import { buildHarborSoftwareApplicationJsonLd } from '@/lib/harbor-facts';
import { getAppStoreUrl, getPlayStoreUrl, getUtmParamsFromUrl } from '@/lib/urls';

export type SeoLandingFeature = {
  icon: IconType;
  title: string;
  description: string;
  bullets?: string[];
  image?: string;
  imageAlt?: string;
};

export type SeoLandingFaq = {
  question: string;
  answer: string;
};

export type SeoLandingCrossLink = {
  href: string;
  label: string;
  description: string;
};

export type SeoLandingPageProps = {
  path: string;
  title: string;
  description: string;
  breadcrumbName: string;
  eyebrow?: string;
  h1: string;
  hero: string;
  screenEvent: string;
  ctaLocation: string;
  storeCampaign: string;
  featuresHeading?: string;
  features: SeoLandingFeature[];
  faqs: SeoLandingFaq[];
  crossLinks?: SeoLandingCrossLink[];
};

function breadcrumbJsonLd(path: string, name: string) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://harborcoparent.com/' },
      { '@type': 'ListItem', position: 2, name, item: `https://harborcoparent.com${path}` },
    ],
  };
}

function faqJsonLd(faqs: SeoLandingFaq[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  };
}

function handleAppStoreClick(location: string) {
  track('Click App Store', { location });
}

function handleGooglePlayClick(location: string) {
  track('Click Google Play', { location });
}

function handleSignUpClick(location: string) {
  track('Click Sign Up', { location });
}

export function SeoLandingPage({
  path,
  title,
  description,
  breadcrumbName,
  eyebrow,
  h1,
  hero,
  screenEvent,
  ctaLocation,
  storeCampaign,
  featuresHeading = 'What Harbor helps you do',
  features,
  faqs,
  crossLinks = [],
}: SeoLandingPageProps) {
  useEffect(() => {
    track(screenEvent);
  }, [screenEvent]);

  const utm = getUtmParamsFromUrl();
  const jsonLd = [
    breadcrumbJsonLd(path, breadcrumbName),
    faqJsonLd(faqs),
    buildHarborSoftwareApplicationJsonLd(),
  ];

  return (
    <div className='min-h-screen bg-white'>
      <SEO title={title} description={description} path={path} jsonLd={jsonLd} />
      {jsonLd.map((data, index) => (
        <script
          key={index}
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(data).replace(/</g, '\\u003c'),
          }}
        />
      ))}
      <MarketingHeader />

      <section className='bg-primary/5 pt-32 pb-12'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          {eyebrow ? (
            <p className='mb-3 text-sm font-semibold uppercase tracking-wide text-primary'>
              {eyebrow}
            </p>
          ) : null}
          <h1 className='mb-4 text-4xl font-bold text-gray-900 md:text-5xl'>{h1}</h1>
          <p className='mx-auto max-w-2xl text-xl leading-relaxed text-gray-600'>{hero}</p>
          <ProductFactBlurb />
          <StoreCtas
            campaign={storeCampaign}
            location={ctaLocation}
            utm={utm}
            variant='hero'
          />
        </div>
      </section>

      <section className='mx-auto max-w-5xl px-6 py-16'>
        <h2 className='mb-10 text-center text-3xl font-bold text-gray-900'>{featuresHeading}</h2>
        <div className='space-y-16'>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const imageOnRight = index % 2 === 1;
            return (
              <article
                key={feature.title}
                className={`grid items-center gap-10 ${feature.image ? 'lg:grid-cols-2' : ''}`}
              >
                {feature.image ? (
                  <div className={`flex justify-center ${imageOnRight ? 'lg:order-2' : ''}`}>
                    <img
                      src={feature.image}
                      alt={feature.imageAlt ?? feature.title}
                      className='h-auto w-full max-w-sm object-contain'
                      style={{ maxHeight: '400px' }}
                    />
                  </div>
                ) : null}
                <div>
                  <div className='mb-5 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10'>
                    <Icon className='h-7 w-7 text-primary' />
                  </div>
                  <h3 className='text-2xl font-bold text-gray-900 md:text-3xl'>{feature.title}</h3>
                  <p className='mt-3 text-lg leading-relaxed text-gray-600'>
                    {feature.description}
                  </p>
                  {feature.bullets?.length ? (
                    <ul className='mt-5 space-y-3'>
                      {feature.bullets.map((bullet) => (
                        <li key={bullet} className='flex items-start gap-3 text-gray-600'>
                          <FiCheckCircle className='mt-0.5 h-5 w-5 shrink-0 text-primary' />
                          <span>{bullet}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {crossLinks.length > 0 ? (
        <section className='bg-sky-light py-14'>
          <div className='mx-auto max-w-5xl px-6'>
            <h2 className='mb-8 text-center text-2xl font-bold text-gray-900'>Related Harbor tools</h2>
            <div className='grid gap-6 md:grid-cols-2'>
              {crossLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className='rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md'
                >
                  <h3 className='text-lg font-semibold text-primary'>{link.label}</h3>
                  <p className='mt-2 text-gray-600'>{link.description}</p>
                </a>
              ))}
            </div>
          </div>
        </section>
      ) : null}

      <section className='mx-auto max-w-4xl px-6 py-16'>
        <h2 className='mb-8 text-center text-3xl font-bold text-gray-900'>Common questions</h2>
        <div className='space-y-6'>
          {faqs.map((faq) => (
            <article key={faq.question} className='rounded-xl border border-gray-200 bg-gray-50 p-6'>
              <h3 className='text-lg font-semibold text-gray-900'>{faq.question}</h3>
              <p className='mt-2 leading-relaxed text-gray-600'>{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className='bg-primary py-14'>
        <div className='mx-auto max-w-4xl px-6 text-center'>
          <h2 className='text-3xl font-bold text-white'>Try Harbor free</h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-white/85'>
            Download Harbor on iOS or Android, or sign up on web. Incoming campaign tags stay on
            the store links.
          </p>
          <StoreCtas
            campaign={storeCampaign}
            location={`${ctaLocation} Footer`}
            utm={utm}
            variant='footer'
          />
        </div>
      </section>

      <Footer />
    </div>
  );
}

function StoreCtas({
  campaign,
  location,
  utm,
  variant,
}: {
  campaign: string;
  location: string;
  utm: ReturnType<typeof getUtmParamsFromUrl>;
  variant: 'hero' | 'footer';
}) {
  const isFooter = variant === 'footer';
  const primaryClass = isFooter
    ? 'inline-flex items-center justify-center gap-2 rounded-lg bg-white px-5 py-3 font-semibold text-primary hover:bg-slate-100'
    : 'inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-3 font-semibold text-white hover:bg-primary-hover';
  const signUpClass = isFooter
    ? 'inline-flex items-center justify-center rounded-lg border border-white px-5 py-3 font-semibold text-white hover:bg-white/10'
    : 'inline-flex items-center justify-center rounded-lg border-2 border-primary px-5 py-3 font-semibold text-primary hover:bg-primary/5';

  return (
    <div className={`flex flex-col justify-center gap-3 sm:flex-row ${isFooter ? 'mt-8' : 'mt-10'}`}>
      <a
        href={getAppStoreUrl(campaign, utm)}
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => handleAppStoreClick(location)}
        className={primaryClass}
      >
        <FaApple size={18} />
        App Store
      </a>
      <a
        href={getPlayStoreUrl(campaign, utm)}
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => handleGooglePlayClick(location)}
        className={primaryClass}
      >
        <FaGooglePlay size={18} />
        Google Play
      </a>
      <Link to='/auth/sign-up' onClick={() => handleSignUpClick(location)} className={signUpClass}>
        Sign up on web
      </Link>
    </div>
  );
}
